import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase environment variables are missing');
  throw new Error('Supabase environment variables are missing');
}

// Create a singleton instance of the Supabase client
let supabaseInstance: ReturnType<typeof createClient> | null = null;

export const getSupabaseClient = () => {
  if (!supabaseInstance) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: window.localStorage,
        storageKey: 'supabase.auth.token',
        debug: import.meta.env.MODE === 'development'
      },
      global: {
        headers: {
          'x-client-info': 'legal-nest-frontend'
        },
        fetch: (...args) => {
          const [resource, config] = args;
          const controller = new AbortController();
          const timeout = setTimeout(() => {
            controller.abort();
          }, 8000); // 8 second timeout
          
          return fetch(resource, {
            ...config,
            signal: controller.signal
          }).finally(() => {
            clearTimeout(timeout);
          });
        }
      },
      realtime: {
        timeout: 10000
      }
    });
  }
  return supabaseInstance;
};

// Export the singleton instance
export const supabase = getSupabaseClient();

// Enhanced initialization with better error handling and timeout
export const initializeSupabaseAuth = async () => {
  try {
    console.log("Initializing Supabase auth...");
    
    // First check if we're online before attempting auth
    if (!navigator.onLine) {
      console.warn("Device is offline, skipping auth initialization");
      return { session: null, user: null, error: "Device offline" };
    }
    
    // Create a promise with longer timeout for slow connections
    const authPromise = new Promise(async (resolve, reject) => {
      try {
        const { data, error } = await supabase.auth.getSession();
        resolve({ data, error });
      } catch (err) {
        reject(err);
      }
    });
    
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Auth initialization timed out')), 12000) // Longer timeout
    );
    
    const result = await Promise.race([authPromise, timeoutPromise])
      .catch(err => {
        console.warn("Auth initialization timeout, continuing with app", err);
        return { data: { session: null }, error: err };
      });
      
    // TypeScript type assertion
    const { data, error } = result as any;
    
    if (error && error.message !== 'Auth initialization timed out') {
      console.error("Supabase auth initialization error:", error);
    }
    
    if (data?.session) {
      console.log("Initial session found:", data.session.user.id);
      
      // Check user data in local storage first if session exists
      const cachedUser = localStorage.getItem('supabase.auth.user');
      if (cachedUser) {
        try {
          return { 
            session: data.session, 
            user: JSON.parse(cachedUser),
            error: null 
          };
        } catch (e) {
          console.warn("Failed to parse cached user data");
        }
      }
      
      // If no cached user or parsing failed, get from API
      try {
        const { data: userData } = await supabase.auth.getUser();
        return { 
          session: data.session, 
          user: userData?.user || null, 
          error: null 
        };
      } catch (userError) {
        console.warn("Failed to fetch user data after session found:", userError);
        return { 
          session: data.session,
          user: data.session.user,
          error: null
        };
      }
    }
    
    console.log("No session found during initialization");
    return { session: null, user: null, error: null };
  } catch (error) {
    console.error("Error in initializeSupabaseAuth:", error);
    // Return null session but don't block app initialization
    return { session: null, user: null, error };
  }
};

// Helper function to check if a user is authenticated with timeout
export const isAuthenticated = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const { data } = await supabase.auth.getSession();
    
    clearTimeout(timeoutId);
    return !!data?.session;
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

// Export useful types
export type { 
  Session, 
  User, 
  UserResponse,
  AuthResponse
} from '@supabase/supabase-js';